<section class="svc" [id]="anchor" #root aria-labelledby="svc-title">
  <!-- ============================================================
       BACKGROUND — soft mesh, floating dots, cursor spotlight
       ============================================================ -->
  <div class="svc__bg" aria-hidden="true">
    <span class="svc__mesh"></span>
    <span class="svc__blob svc__blob--pink"></span>
    <span class="svc__blob svc__blob--violet"></span>
    <span class="svc__arc svc__arc--1"></span>
    <span class="svc__arc svc__arc--2"></span>

    <span class="svc__motes">
      <i></i><i></i><i></i><i></i><i></i><i></i>
      <i></i><i></i><i></i><i></i><i></i><i></i>
    </span>
  </div>

  <!-- follows the cursor across the whole section -->
  <span class="svc__spot" #spot aria-hidden="true"></span>

  <div class="container svc__inner">
    <!-- ============================================================
         HEADER
         ============================================================ -->
    <header class="svc__head" #head>
      <span class="svc__badge">
        <svg class="svc__badge-spark" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.9L12 2.5z" />
        </svg>
        WHAT WE DO
      </span>

      <h2 class="svc__title" id="svc-title" #title>
        Digital Solutions Built For
        <span class="svc__title-grad">Modern Businesses</span>
      </h2>

      <p class="svc__lede">
        Helping startups and enterprises transform ideas into scalable digital
        products.
      </p>
    </header>

    <!-- ============================================================
         ASYMMETRIC GRID
         ============================================================ -->
    <div class="svc__grid" #grid>
      <!-- ---------- FEATURED ---------- -->
      <a
        class="svc__feat"
        #card
        [routerLink]="featured.link"
        [attr.aria-label]="featured.name"
      >
        <span class="svc__feat-edge" aria-hidden="true"></span>
        <span class="svc__sheen" aria-hidden="true"></span>

        <div class="svc__feat-copy">
          <span class="svc__tag">FEATURED</span>
          <h3 class="svc__feat-title">{{ featured.name }}</h3>
          <span class="svc__rule" aria-hidden="true"></span>
          <p class="svc__feat-desc">{{ featured.desc }}</p>

          <span class="svc__cta">
            {{ featured.cta }}
            <i aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </i>
          </span>
        </div>

        <!-- ---------- illustration: laptop + floating UI ---------- -->
        <figure class="svc__art" aria-hidden="true">
          <span class="svc__art-glow"></span>

          <svg class="svc__laptop" viewBox="0 0 340 250" fill="none">
            <defs>
              <linearGradient id="svcScreen" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="#241b4d" />
                <stop offset="100%" stop-color="#140f2e" />
              </linearGradient>
              <linearGradient id="svcLid" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="#c9cdd8" />
                <stop offset="48%" stop-color="#9aa1b1" />
                <stop offset="100%" stop-color="#c3c8d4" />
              </linearGradient>
              <linearGradient id="svcBeam" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#ff3d9a" stop-opacity="0" />
                <stop offset="50%" stop-color="#ff3d9a" stop-opacity=".9" />
                <stop offset="100%" stop-color="#8a5cff" stop-opacity="0" />
              </linearGradient>
            </defs>

            <!-- lid -->
            <rect x="66" y="34" width="208" height="140" rx="9" fill="url(#svcLid)" />
            <rect x="72" y="40" width="196" height="128" rx="6" fill="url(#svcScreen)" />

            <!-- dashboard inside the screen -->
            <g class="svc__ui">
              <rect x="80" y="48" width="52" height="112" rx="4" fill="#ffffff" fill-opacity=".07" />
              <rect x="86" y="56" width="30" height="4" rx="2" fill="#ff3d9a" fill-opacity=".85" />
              <rect x="86" y="68" width="38" height="3" rx="1.5" fill="#ffffff" fill-opacity=".3" />
              <rect x="86" y="78" width="30" height="3" rx="1.5" fill="#ffffff" fill-opacity=".22" />
              <rect x="86" y="88" width="34" height="3" rx="1.5" fill="#ffffff" fill-opacity=".22" />

              <rect x="140" y="48" width="56" height="34" rx="4" fill="#ffffff" fill-opacity=".08" />
              <rect x="204" y="48" width="56" height="34" rx="4" fill="#ffffff" fill-opacity=".08" />
              <rect x="146" y="56" width="26" height="3" rx="1.5" fill="#8a5cff" />
              <rect x="210" y="56" width="26" height="3" rx="1.5" fill="#3b82f6" />

              <!-- bars that keep growing -->
              <g class="svc__bars">
                <rect class="svc__bar" x="148" y="128" width="8" height="26" rx="2" fill="#ff3d9a" fill-opacity=".8" />
                <rect class="svc__bar" x="162" y="118" width="8" height="36" rx="2" fill="#8a5cff" fill-opacity=".8" />
                <rect class="svc__bar" x="176" y="134" width="8" height="20" rx="2" fill="#3b82f6" fill-opacity=".8" />
                <rect class="svc__bar" x="190" y="110" width="8" height="44" rx="2" fill="#8a5cff" fill-opacity=".8" />
              </g>

              <rect x="204" y="92" width="56" height="62" rx="4" fill="#ffffff" fill-opacity=".07" />
              <circle cx="232" cy="118" r="16" fill="none" stroke="#8a5cff" stroke-width="5" stroke-opacity=".55" />
              <path class="svc__ring" d="M232 102a16 16 0 0 1 13 25" fill="none" stroke="#ff3d9a" stroke-width="5" stroke-linecap="round" />
              <rect x="212" y="142" width="40" height="3" rx="1.5" fill="#ffffff" fill-opacity=".24" />
            </g>

            <!-- base -->
            <path d="M40 178h260l16 18H24z" fill="url(#svcLid)" />
            <rect x="150" y="182" width="40" height="4" rx="2" fill="#8f96a6" />

            <!-- light beam sweeping under the machine -->
            <path class="svc__beam" d="M28 206c70-26 210-26 290 4" stroke="url(#svcBeam)" stroke-width="3" fill="none" stroke-linecap="round" />
          </svg>

          <!-- floating UI chips -->
          <span class="svc__chip svc__chip--chart">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 20V6M4 20h16M9 20v-7M14 20v-11M19 20v-5" />
            </svg>
          </span>
          <span class="svc__chip svc__chip--code">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m9 8-5 4 5 4M15 8l5 4-5 4" />
            </svg>
          </span>
          <span class="svc__chip svc__chip--pie">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3v9h9" /><circle cx="12" cy="12" r="9" />
            </svg>
          </span>
          <span class="svc__chip svc__chip--gear">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3.2" />
              <path d="M12 3v2.2M12 18.8V21M21 12h-2.2M5.2 12H3M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6M18.4 18.4l-1.6-1.6M7.2 7.2 5.6 5.6" />
            </svg>
          </span>
        </figure>
      </a>

      <!-- ---------- SUPPORTING CARDS ---------- -->
      <a
        *ngFor="let s of items; trackBy: trackSvc"
        #card
        class="svc__card"
        [class.svc__card--wide]="s.slot === 'wide'"
        [ngClass]="'svc__card--' + s.slot"
        [routerLink]="s.link"
      >
        <span class="svc__card-edge" aria-hidden="true"></span>
        <span class="svc__sheen" aria-hidden="true"></span>

        <span class="svc__ico" aria-hidden="true">
          <ng-container [ngSwitch]="s.icon">
            <svg *ngSwitchCase="'monitor'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2.5" y="4" width="19" height="13" rx="2.2" />
              <path d="M9 21h6M12 17v4" />
              <path d="m10 8-2 2.5L10 13M14 8l2 2.5L14 13" />
            </svg>
            <svg *ngSwitchCase="'mobile'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <rect x="6.5" y="2.5" width="11" height="19" rx="2.6" />
              <path d="M10.5 5.5h3M12 18.5h.01" />
            </svg>
            <svg *ngSwitchCase="'megaphone'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 10v4a2 2 0 0 0 2 2h2l8 4V4L8 8H6a2 2 0 0 0-2 2z" />
              <path d="M19 9a4 4 0 0 1 0 6" />
            </svg>
            <svg *ngSwitchCase="'search'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="10.5" cy="10.5" r="6.5" />
              <path d="m15.5 15.5 5 5" />
            </svg>
            <svg *ngSwitchDefault viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 13v-1a8 8 0 0 1 16 0v1" />
              <rect x="2.5" y="13" width="4.5" height="6.5" rx="2" />
              <rect x="17" y="13" width="4.5" height="6.5" rx="2" />
              <path d="M20 19.5a2.5 2.5 0 0 1-2.5 2.5H13" />
            </svg>
          </ng-container>
        </span>

        <h3 class="svc__name">{{ s.name }}</h3>
        <span class="svc__rule" aria-hidden="true"></span>
        <p class="svc__desc">{{ s.desc }}</p>

        <span class="svc__arrow" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </span>
      </a>
    </div>

   
  </div>
</section>
